import { getMarketingConsent } from './analytics'

const META_SCRIPT_ID = 'sugar-gremlin-meta-pixel'
const META_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'
const META_PIXEL_ID_PATTERN = /^\d{8,25}$/

type Fbq = {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: (...args: unknown[]) => void
  loaded: boolean
  version: string
  disablePushState?: boolean
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

let metaActive = false
let activePixelId: string | null = null
const initialisedPixelIds = new Set<string>()
const pageViewKeys = new Set<string>()

function normalisePixelId(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  return META_PIXEL_ID_PATTERN.test(trimmed) ? trimmed : null
}

export function getMetaPixelId(): string | null {
  return normalisePixelId(import.meta.env.VITE_META_PIXEL_ID)
}

function ensureFbq(): Fbq {
  if (window.fbq) return window.fbq

  let fbq: Fbq
  fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
      return
    }
    fbq.queue.push(args)
  }) as Fbq

  fbq.queue = []
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq
  window._fbq = fbq
  return fbq
}

function injectMetaScript(): void {
  if (document.getElementById(META_SCRIPT_ID)) return

  const script = document.createElement('script')
  script.id = META_SCRIPT_ID
  script.async = true
  script.src = META_SCRIPT_SRC
  script.dataset.trackingPurpose = 'advertising-measurement'
  document.head.appendChild(script)
}

function removeMetaCookies(): void {
  const names = ['_fbp', '_fbc']
  const domains = [window.location.hostname]
  if (window.location.hostname === 'sugargremlin.co.nz' || window.location.hostname.endsWith('.sugargremlin.co.nz')) {
    domains.push('.sugargremlin.co.nz')
  }

  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
    }
  }
}

function currentPageKey(): string {
  // Query-string changes on checkout must not create duplicate SPA-style PageViews.
  return window.location.pathname
}

function sendPageViewOnce(fbq: Fbq): boolean {
  const pageKey = currentPageKey()
  if (pageViewKeys.has(pageKey)) return false

  fbq('track', 'PageView')
  pageViewKeys.add(pageKey)
  return true
}

export function startMetaPixel(): boolean {
  if (typeof window === 'undefined' || getMarketingConsent() !== 'granted') return false

  const pixelId = getMetaPixelId()
  if (!pixelId) return false

  const fbq = ensureFbq()

  // Keep automatic configuration and automatic history tracking off. We do
  // not pass advanced-matching data and do not permit automatic checkout-field
  // or PII collection.
  fbq.disablePushState = true
  fbq('set', 'autoConfig', false, pixelId)
  fbq('consent', 'grant')

  if (!initialisedPixelIds.has(pixelId)) {
    fbq('init', pixelId)
    initialisedPixelIds.add(pixelId)
  }

  injectMetaScript()
  activePixelId = pixelId
  metaActive = true
  sendPageViewOnce(fbq)
  return true
}

export function stopMetaPixel({ preserveCookies = false }: { preserveCookies?: boolean } = {}): void {
  if (typeof window === 'undefined') return

  if (window.fbq) {
    window.fbq('consent', 'revoke')
  }

  document.getElementById(META_SCRIPT_ID)?.remove()
  if (!preserveCookies) removeMetaCookies()
  metaActive = false
  activePixelId = null
  initialisedPixelIds.clear()

  try {
    delete window.fbq
    delete window._fbq
  } catch {
    window.fbq = undefined
    window._fbq = undefined
  }
}

export type MetaPurchaseQueueState = 'queued' | 'not-permitted' | 'not-configured'

export function trackMetaPurchase(
  parameters: Record<string, unknown>,
  eventId: string,
): MetaPurchaseQueueState {
  if (typeof window === 'undefined' || getMarketingConsent() !== 'granted') return 'not-permitted'
  if (!getMetaPixelId()) return 'not-configured'

  if (!metaActive || !activePixelId || !window.fbq) {
    if (!startMetaPixel()) return getMarketingConsent() === 'granted' ? 'not-configured' : 'not-permitted'
  }

  if (getMarketingConsent() !== 'granted') return 'not-permitted'
  if (!window.fbq) return 'not-configured'

  window.fbq('track', 'Purchase', parameters, { eventID: eventId })
  return 'queued'
}

export function trackMetaEvent(
  eventName: 'ViewContent' | 'AddToCart' | 'InitiateCheckout' | 'Purchase',
  parameters: Record<string, unknown> = {},
  eventId?: string,
): boolean {
  // Hard gate every call against the current marketing choice, even if Meta
  // was previously initialised earlier in the browser session.
  if (typeof window === 'undefined' || getMarketingConsent() !== 'granted') return false

  if (!metaActive || !activePixelId || !window.fbq) {
    if (!startMetaPixel()) return false
  }

  if (getMarketingConsent() !== 'granted' || !window.fbq) return false

  if (eventId) {
    window.fbq('track', eventName, parameters, { eventID: eventId })
  } else {
    window.fbq('track', eventName, parameters)
  }
  return true
}

export function isMetaPixelActive(): boolean {
  return metaActive && getMarketingConsent() === 'granted'
}
